import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { WorkBook } from 'xlsx';
import axios from 'axios';
import { ProductImportApi, ImportPreview, ImportReceipt, ImportItem } from '../api/ProductImportApi';
import { csvCell, IMPORT_BATCH_SIZE, importFields, loadSpreadsheet, mapRows, Mapping, readTable, suggestMapping } from '../utils/productImport';
import './AssistantImport.css';
import { notifyInventoryChanged } from '../utils/inventoryEvents';

interface Props { companyId: string; depositId: string; aiAvailable: boolean; onBusy: (busy: boolean) => void; onClose: () => void }
export default function AssistantImport({ companyId, depositId, aiAvailable, onBusy, onClose }: Props) {
  const { t } = useTranslation();
  const [workbook, setWorkbook] = useState<WorkBook>();
  const [sheet, setSheet] = useState('');
  const [header, setHeader] = useState(1);
  const [table, setTable] = useState<Awaited<ReturnType<typeof readTable>>>();
  const [mapping, setMapping] = useState<Mapping>({});
  const [mode, setMode] = useState('catalog');
  const [preview, setPreview] = useState<ImportPreview>();
  const [receipt, setReceipt] = useState<ImportReceipt>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fileName, setFileName] = useState('');
  const [reading, setReading] = useState(false);
  const [fileError, setFileError] = useState('');
  const [completedRows, setCompletedRows] = useState(0);
  const [savedScope, setSavedScope] = useState('');
  const [excluded, setExcluded] = useState<ImportItem[]>([]);
  const [skipped, setSkipped] = useState<ImportItem[]>([]);
  const batchRows = table?.rows.slice(completedRows, completedRows + IMPORT_BATCH_SIZE) || [];
  const scope = `${companyId}:${mode === 'stock' ? depositId : ''}`;
  const scopeChanged = completedRows > 0 && savedScope !== scope;
  const hasRemaining = !!table && completedRows < table.rows.length;
  const errorMessage = useRef<HTMLParagraphElement>(null);
  const alive = useRef(true);
  useEffect(() => { if (error) errorMessage.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' }); }, [error]);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => { setPreview(undefined); setExcluded([]); setError(''); }, [companyId, depositId]);
  const tr = (key: string) => t(`import.${key}`);
  useEffect(() => {
    let active = true;
    setTable(undefined); setPreview(undefined); setExcluded([]); setMapping({}); setError('');
    setReading(!!workbook);
    if (workbook) setFileError('');
    if (workbook) readTable(workbook, sheet, header).then(result => {
      if (active) { setTable(result); setMapping(suggestMapping(result.headers)); }
    }).catch(failure => { if (active) {
      const message = t(`import.${failure.message}`, { defaultValue: t('import.invalidFile') });
      setFileError(message); setError(message);
    } }).finally(() => { if (active) setReading(false); });
    return () => { active = false; };
  }, [workbook, sheet, header, t]);
  const run = async (action: () => Promise<void>) => {
    if (busy) return;
    setBusy(true); onBusy(true); setError('');
    try { await action(); }
    catch (failure) {
      if (alive.current) {
        const message = axios.isAxiosError(failure) ? failure.response?.data?.message : undefined;
        setError(typeof message === 'string' ? message : tr('failed'));
      }
    } finally { if (alive.current) { setBusy(false); onBusy(false); } }
  };
  const download = (errorsOnly: boolean, items = preview?.items || []) => {
    const rows = items.filter(item => !errorsOnly || item.error);
    const contents = [['Row', 'Product', 'Barcode', 'Action', 'Quantity to add', 'Error'], ...rows.map(item => [item.row, item.name, item.barcode, item.action, item.quantity, item.error])];
    const url = URL.createObjectURL(new Blob(['\uFEFF' + contents.map(row => row.map(csvCell).join(',')).join('\r\n')], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a'); link.href = url; link.download = errorsOnly ? 'import-errors.csv' : 'import-preview.csv'; link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return <section className="assistant-import" aria-label={tr('title')} aria-busy={busy}>
    <h3>{tr('title')}</h3><p>{tr('intro')}</p>
    {!companyId && <p role="status">{tr('chooseCompany')}</p>}
    {(excluded.length > 0 || skipped.length > 0) && <div className="assistant-result" role="status">
      <p>{t('import.skippedSummary', { saved: completedRows - skipped.length, skipped: skipped.length, pending: excluded.length })}</p>
      <button type="button" onClick={() => download(true, [...skipped, ...excluded])}>{tr('downloadErrors')}</button>
    </div>}
    {table && table.rows.length > IMPORT_BATCH_SIZE && <div className="assistant-result" role="status">
      <strong>{t('import.batchProgress', { completed: completedRows, total: table.rows.length })}</strong>
      {hasRemaining && <p>{t('import.batchRange', { batch: Math.floor(completedRows / IMPORT_BATCH_SIZE) + 1, batches: Math.ceil(table.rows.length / IMPORT_BATCH_SIZE), first: batchRows[0]?.row, last: batchRows[batchRows.length - 1]?.row })}</p>}
      <small>{tr('batchHelp')}</small>
    </div>}
    {scopeChanged && <p role="alert" className="assistant-error">{tr('batchScope')}</p>}
    {receipt ? <div role="status"><h4>{tr(hasRemaining ? 'batchComplete' : skipped.length ? 'completeWithErrors' : 'complete')}</h4><p>{t('import.summary', { ...receipt })}</p><small>{receipt.id}</small>
      {hasRemaining && <button type="button" disabled={busy || reading || scopeChanged} onClick={() => { setReceipt(undefined); setPreview(undefined); setExcluded([]); setError(''); }}>{tr('nextBatch')}</button>}
    </div> : <>
      <fieldset disabled={busy || !!preview?.id}>
        <label>{tr('mode')}<select disabled={completedRows > 0} value={mode} onChange={e => { setMode(e.target.value); setPreview(undefined); }}>
          <option value="catalog">{tr('catalog')}</option><option value="stock">{tr('stock')}</option>
        </select></label>
        <p>{mode === 'stock' ? tr('stockHelp') : tr('catalogHelp')}</p>
        {mode === 'stock' && !depositId && <p>{tr('chooseDeposit')}</p>}
        <label>{tr('file')}<input type="file" disabled={completedRows > 0} accept=".xlsx,.csv" onChange={e => {
          const file = e.target.files?.[0]; if (!file) return;
          setWorkbook(undefined); setTable(undefined); setPreview(undefined); setReceipt(undefined); setFileName(file.name); setFileError('');
          void run(async () => { try { const loaded = await loadSpreadsheet(file); if (alive.current) { setHeader(1); setSheet(loaded.SheetNames[0]); setWorkbook(loaded); } }
            catch (failure) { if (alive.current) {
              const message = t(`import.${failure instanceof Error ? failure.message : 'invalidFile'}`, { defaultValue: tr('invalidFile') });
              setFileError(message); setError(message);
            } } });
        }} /></label>
        {workbook && <div className="assistant-import-grid">
          <label>{tr('sheet')}<select disabled={completedRows > 0} value={sheet} onChange={e => setSheet(e.target.value)}>{workbook.SheetNames.map(name => <option key={name}>{name}</option>)}</select></label>
          <label>{tr('header')}<input disabled={completedRows > 0} type="number" min="1" max="20" value={header} onChange={e => setHeader(Number(e.target.value))} /></label>
        </div>}
        {table && <><p>{fileName} · {t('import.rows', { count: table.rows.length })}</p>
          <h4>{tr('mapping')}</h4><p>{tr('mappingHelp')}</p>
          {aiAvailable && <button type="button" onClick={() => void run(async () => { const result = await ProductImportApi.suggest(table.headers); if (alive.current) { setMapping(result.data); setPreview(undefined); } })}>{tr('suggest')}</button>}
          <div className="assistant-import-grid">{importFields.map(field => <label key={field}>{tr(field)}{field === 'name' || (field === 'quantity' && mode === 'stock') ? ' *' : ''}
            <select value={mapping[field] ?? ''} onChange={e => { setMapping({ ...mapping, [field]: e.target.value === '' ? undefined : Number(e.target.value) }); setPreview(undefined); }}>
              <option value="">{tr('unmapped')}</option>{table.headers.map((name, index) => <option key={index} value={index}>{index + 1}: {name || tr('unnamed')}</option>)}
            </select>
            {mapping[field] !== undefined && <small>{tr('sample')}: {table.rows[0].cells[mapping[field]!] || '—'}</small>}
          </label>)}</div>
        </>}
      </fieldset>
      {!preview?.id && <button type="button" disabled={busy || reading}
        onClick={() => {
          const missing: string[] = [];
          if (scopeChanged) missing.push(tr('batchScope'));
          if (!companyId) missing.push(tr('chooseCompany'));
          if (!table) missing.push(fileError || tr(workbook ? 'empty' : 'chooseFile'));
          if (table && mapping.name === undefined) missing.push(tr('missingName'));
          if (mode === 'stock' && !depositId) missing.push(tr('chooseDeposit'));
          if (table && mode === 'stock' && mapping.quantity === undefined) missing.push(tr('missingQuantity'));
          const selected = Object.values(mapping).filter(value => value !== undefined);
          if (new Set(selected).size !== selected.length) missing.push(tr('duplicateMapping'));
          if (missing.length) { setError(missing.join('\n')); return; }
          void run(async () => {
          setExcluded([]);
          const result = await ProductImportApi.preview({ companyId: Number(companyId), depositId: mode === 'stock' ? Number(depositId) : undefined, mode, rows: mapRows(batchRows, mapping) });
          if (alive.current) setPreview(result.data);
        }); }}>{tr('preview')}</button>}
      {error && <p ref={errorMessage} role="alert" className="assistant-error assistant-import-error">{error}</p>}
      {preview && <div className="assistant-result"><h4>{tr('preview')}</h4>
        <p>{t('import.validation', { count: preview.items.length, errors: preview.errors })}</p>
        <p>{tr('unchanged')}</p>
        <div className="assistant-table-scroll"><table><thead><tr><th>{tr('row')}</th><th>{tr('name')}</th><th>{tr('action')}</th><th>{tr('quantity')}</th><th>{tr('errors')}</th></tr></thead>
          <tbody>{preview.items.map((item, index) => <tr key={index}><td>{item.row}</td><td>{item.name}{item.productId != null && <small> #{item.productId}</small>}</td><td>{tr(item.action)}</td><td>{item.quantity}</td><td>{item.error}</td></tr>)}</tbody></table></div>
        <button type="button" onClick={() => download(false)}>{tr('downloadPreview')}</button>
        {preview.errors > 0 && <button type="button" onClick={() => download(true)}>{tr('downloadErrors')}</button>}
        {preview.errors > 0 && preview.items.some(item => !item.error) && <>
          <p>{tr('validOnlyHelp')}</p>
          <button type="button" disabled={busy || scopeChanged} onClick={() => void run(async () => {
            const validRows = new Set(preview.items.filter(item => !item.error).map(item => item.row));
            const rejected = preview.items.filter(item => !!item.error);
            const result = await ProductImportApi.preview({ companyId: Number(companyId), depositId: mode === 'stock' ? Number(depositId) : undefined, mode,
              rows: mapRows(batchRows.filter(row => validRows.has(row.row)), mapping) });
            if (alive.current) { setExcluded(previous => [...previous, ...rejected]); setPreview(result.data); }
          })}>{tr('previewValidOnly')}</button>
        </>}
        {preview.id && <><p>{tr('confirmHelp')}</p><button type="button" disabled={busy} onClick={() => void run(async () => {
          const result = await ProductImportApi.confirm(preview.id!);
          notifyInventoryChanged({ companyId: Number(companyId), depositId: mode === 'stock' ? Number(depositId) : undefined });
          if (alive.current) {
            setReceipt(result.data); setSavedScope(scope); setCompletedRows(value => value + batchRows.length);
            setSkipped(previous => [...previous, ...excluded]); setExcluded([]);
          }
        })}>{tr('confirm')}</button><button type="button" disabled={busy} onClick={() => setPreview(undefined)}>{tr('edit')}</button></>}
      </div>}
    </>}
    {(busy || reading) && <p role="status">{tr('working')}</p>}
    <button type="button" disabled={busy} onClick={onClose}>{tr('back')}</button>
  </section>;
}
